import { Select, Text } from "@mantine/core";
import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { useGetApi, useIsOnline } from "src/hooks";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setSigningOverride } from "src/redux/slices/generatorSlice";
import { Flex } from "src/styled";
import { MEDLBID } from "src/utils";

import { mapGetSigners } from "../../pages/Settings/AdvancedFeatures/LogbookSigners/LogbookSigners.utils";

const NOT_SET_VALUE = "__NOT_SET__";
const LOCKED_TO_ME_VALUE = "*SELF*";

const overrideToSelectValue = (override: string | null | undefined): string => {
  if (override == null || override === "") return NOT_SET_VALUE;
  return override;
};

const selectValueToOverride = (value: string | null): string | null => {
  if (!value || value === NOT_SET_VALUE) return null;
  return value;
};

interface Props {
  style?: React.CSSProperties;
}

const MedSigningOverrideSelect = ({ style }: Props) => {
  const { logbookId } = useParams();
  const dispatch = useDispatch();
  const isOnline = useIsOnline();
  const signingOverride = useSelector(
    state => state.generatorSlice.lbentry.signingOverride,
  );
  const isReadonly = useSelector(
    state => state.generatorSlice.lbentry.isReadonly,
  );

  const { data } = useGetApi({
    request: "get-signers",
    mapper: mapGetSigners,
    fetchOnInit: logbookId === MEDLBID,
  });

  const [value, setValue] = useState<string>(
    overrideToSelectValue(signingOverride),
  );

  useEffect(() => {
    setValue(overrideToSelectValue(signingOverride));
  }, [signingOverride]);

  const options = useMemo(() => {
    const base = [
      { value: NOT_SET_VALUE, label: "Not Set" },
      { value: LOCKED_TO_ME_VALUE, label: "Locked to me" },
    ];

    const medSigners =
      data?.rows
        ?.filter(row => row.lbid === MEDLBID && row.name?.trim())
        .map(row => ({
          value: row.name.trim(),
          label: row.name.trim(),
        })) ?? [];

    const seen = new Set(base.map(o => o.value));
    const uniqueMedSigners = medSigners.filter(o => {
      if (seen.has(o.value)) return false;
      seen.add(o.value);
      return true;
    });

    return [...base, ...uniqueMedSigners];
  }, [data?.rows]);

  const handleChange = (v: string | null) => {
    console.log(v);
    const next = v ?? NOT_SET_VALUE;
    setValue(next);
    dispatch(setSigningOverride(selectValueToOverride(next)));
  };

  if (logbookId !== MEDLBID) return null;

  return (
    <Flex gap={15} align="center" style={style}>
      <Text>Signing</Text>
      <Select
        data={options}
        value={value}
        onChange={handleChange}
        disabled={!isOnline || isReadonly}
        w={200}
      />
    </Flex>
  );
};

export default MedSigningOverrideSelect;
